import { token } from '@styled-system/tokens';

import type { ProductItemCondition } from '@/shared/api/types/generated';
import i18n from '@/shared/i18n';

const CONDITION_KEYS: Record<ProductItemCondition, string> = {
  veryGoodCondition: 'veryGoodCondition',
  good: 'good',
  goodCondition: 'goodCondition',
  satisfactory: 'satisfactory',
  newWithTag: 'newWithTag',
  newWithoutTag: 'newWithoutTag',
  somePiecesNotWorking: 'somePiecesNotWorking',
  '%future added value': 'unknown',
};

/** Label localisé (fr/en) — évalué à l'appel, suit la langue active. */
export const getItemConditionLabel = (condition: ProductItemCondition): string =>
  i18n.t(`conditions.${CONDITION_KEYS[condition] ?? 'unknown'}`);

/** Garde : la valeur est-elle un enum d'état connu (vs un titre Vinted déjà humain) ? */
export const isKnownItemCondition = (value: string): value is ProductItemCondition => value in CONDITION_KEYS;

/**
 * Titres FRANÇAIS du dictionnaire Vinted (renvoyés par l'API : facettes,
 * stats, feed de veille) → enum. Permet de LOCALISER une donnée serveur.
 */
export const CONDITION_BY_FRENCH_TITLE: Record<string, ProductItemCondition> = {
  'Neuf avec étiquette': 'newWithTag',
  'Neuf sans étiquette': 'newWithoutTag',
  'Très bon état': 'veryGoodCondition',
  'Bon état': 'goodCondition',
  Satisfaisant: 'satisfactory',
  Bon: 'good',
  'Certaines pièces ne fonctionnent pas': 'somePiecesNotWorking',
};

/** Titre d'état serveur (français) → label localisé ; inchangé si inconnu. */
export const localizeServerConditionTitle = (title: string): string => {
  const condition = CONDITION_BY_FRENCH_TITLE[title];
  return condition ? getItemConditionLabel(condition) : title;
};

export const getItemConditionColor = (condition: ProductItemCondition): string => {
  const colors: Record<ProductItemCondition, string> = {
    veryGoodCondition: token.var('colors.accent.green'), // Vert
    good: token.var('colors.accent.green'), // Vert
    goodCondition: token.var('colors.accent.green'), // Vert
    satisfactory: token.var('colors.accent.orange'), // Orange
    newWithTag: token.var('colors.accent.green'), // Vert
    newWithoutTag: token.var('colors.accent.green'), // Vert
    somePiecesNotWorking: token.var('colors.accent.red'), // Rouge
    '%future added value': token.var('colors.font.muted'), // Gris
  };

  return colors[condition] || token.var('colors.font.muted');
};

// Les 5 états standard Vinted, ordonnés du meilleur au pire (l'ordre du
// sélecteur). `good` et `somePiecesNotWorking` sont des valeurs legacy/
// électronique : pas proposées à la sélection.
export const getAvailableItemConditions = (): Array<{ value: ProductItemCondition; label: string }> => {
  const conditions: ProductItemCondition[] = [
    'newWithTag',
    'newWithoutTag',
    'veryGoodCondition',
    'goodCondition',
    'satisfactory',
  ];

  return conditions.map((condition) => ({
    value: condition,
    label: getItemConditionLabel(condition),
  }));
};
